import React, { useEffect, useState } from 'react';
import { Row, Col, FormGroup, Label, Input, Button } from 'reactstrap';
import FileServer from 'file-saver';
import { axiosApi } from '../Basicos/axiosInstances';
import AnaliseVacinacao from './AnaliseVacinacao';
import { ROTULOS_SITUACAO, CORES_SITUACAO } from './vacinacaoConstantes';

const estiloCard = {
    border: '1px solid #e9ecef',
    borderRadius: '.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,.08)',
    padding: '.75rem 1rem',
};

const ITENS_POR_PAGINA = 10;

const normalizarTexto = (texto) => (texto || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export default function SituacaoVacinal() {
    const [campanhas, setCampanhas] = useState([]);
    const [campanhaId, setCampanhaId] = useState('');
    const [semestreId, setSemestreId] = useState('');
    const [turmas, setTurmas] = useState([]);
    const [turmaId, setTurmaId] = useState('');
    const [situacao, setSituacao] = useState('');

    const [todosDocumentos, setTodosDocumentos] = useState([]);
    const [contadores, setContadores] = useState({});
    const [carregando, setCarregando] = useState(false);
    const [buscaRealizada, setBuscaRealizada] = useState(false);

    const [buscaAluno, setBuscaAluno] = useState('');
    const [paginaExibida, setPaginaExibida] = useState(1);

    const [analiseAberta, setAnaliseAberta] = useState(false);
    const [alunoSelecionado, setAlunoSelecionado] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axiosApi.get('/vacinacao/campanha');
                setCampanhas(data.dados || []);
            } catch (error) {
                console.log(error);
            }
        })();
    }, []);

    // Cada item de "campanhas" é uma janela (período letivo + semestre). O 1º select mostra só os
    // períodos letivos que têm alguma janela cadastrada; o 2º mostra só os semestres com janela
    // dentro do período escolhido — nada de listar período/semestre sem campanha.
    const periodosLetivos = [];
    const periodosVistos = new Set();
    campanhas.forEach((campanha) => {
        if (!periodosVistos.has(campanha.campanha_vacinacao_id)) {
            periodosVistos.add(campanha.campanha_vacinacao_id);
            periodosLetivos.push({ id: campanha.campanha_vacinacao_id, descricao: campanha.descricao });
        }
    });

    const semestresDaCampanha = campanhas.filter((campanha) => String(campanha.campanha_vacinacao_id) === String(campanhaId));

    const selecionarCampanha = (valor) => {
        setCampanhaId(valor);
        setSemestreId('');
    };

    useEffect(() => {
        setTurmaId('');

        if (!campanhaId || !semestreId) {
            setTurmas([]);
            return;
        }

        (async () => {
            try {
                const { data } = await axiosApi.get('/vacinacao/turmas', {
                    params: { campanha_id: campanhaId, semestre_id: semestreId },
                });
                setTurmas(data.dados || []);
            } catch (error) {
                console.log(error);
            }
        })();
    }, [campanhaId, semestreId]);

    const filtros = () => ({
        campanha_id: campanhaId,
        semestre_id: semestreId || undefined,
        turma_id: turmaId || undefined,
        situacao: situacao || undefined,
    });

    // Período + Semestre já delimitam um conjunto pequeno o bastante para trazer tudo de uma vez
    // (sem depender da paginação do backend), permitindo filtrar por aluno instantaneamente no front.
    const pesquisar = async (manterBusca = false) => {
        if (!campanhaId || !semestreId) {
            return;
        }

        setCarregando(true);
        try {
            const primeira = await axiosApi.get('/vacinacao/situacao', {
                params: { ...filtros(), page: 1 },
            });
            let documentos = primeira.data.documentos || [];
            const totalPaginas = primeira.data.paginacao?.total_paginas || 1;

            if (totalPaginas > 1) {
                const demaisPaginas = await Promise.all(
                    Array.from({ length: totalPaginas - 1 }, (_, indice) => axiosApi.get('/vacinacao/situacao', {
                        params: { ...filtros(), page: indice + 2 },
                    }))
                );
                demaisPaginas.forEach((resposta) => {
                    documentos = documentos.concat(resposta.data.documentos || []);
                });
            }

            setTodosDocumentos(documentos);
            setContadores(primeira.data.contadores || {});
            if (!manterBusca) {
                setBuscaAluno('');
            }
            setPaginaExibida(1);
            setBuscaRealizada(true);
        } catch (error) {
            console.log(error);
        } finally {
            setCarregando(false);
        }
    };

    const limpar = () => {
        setSemestreId('');
        setTurmaId('');
        setSituacao('');
        setTodosDocumentos([]);
        setContadores({});
        setBuscaAluno('');
        setPaginaExibida(1);
        setBuscaRealizada(false);
    };

    const documentosFiltrados = buscaAluno
        ? todosDocumentos.filter((linha) => {
            const termo = normalizarTexto(buscaAluno);
            return normalizarTexto(linha.nome).includes(termo) || (linha.matricula || '').toLowerCase().includes(termo);
        })
        : todosDocumentos;

    const totalPaginasExibidas = Math.max(1, Math.ceil(documentosFiltrados.length / ITENS_POR_PAGINA));
    const paginaAtual = Math.min(paginaExibida, totalPaginasExibidas);
    const documentosExibidos = documentosFiltrados.slice(
        (paginaAtual - 1) * ITENS_POR_PAGINA,
        paginaAtual * ITENS_POR_PAGINA
    );

    const alterarBuscaAluno = (valor) => {
        setBuscaAluno(valor);
        setPaginaExibida(1);
    };

    const exportar = async () => {
        try {
            const { data } = await axiosApi.get('/vacinacao/situacao/exportar', {
                params: filtros(),
                responseType: 'blob',
            });
            FileServer.saveAs(new File([data], 'situacao-vacinal.xlsx', { type: data.type }));
        } catch (error) {
            console.log(error);
        }
    };

    const abrirAnalise = (linha) => setAlunoSelecionado(linha) || setAnaliseAberta(true);

    const fecharAnalise = (atualizado) => {
        setAnaliseAberta(false);
        setAlunoSelecionado(null);
        if (atualizado) {
            pesquisar(true);
        }
    };

    if (analiseAberta) {
        return (
            <AnaliseVacinacao
                campanhaId={campanhaId}
                alunoId={alunoSelecionado?.aluno_id}
                onVoltar={fecharAnalise}
            />
        );
    }

    return (
        <Col sm={12} md={12} className="col-conteudo p-4">
            <Row form className="align-items-end mt-3">
                <Col xs={12} md={3}>
                    <FormGroup>
                        <Label>Período Letivo*</Label>
                        <Input type="select" value={campanhaId} onChange={(e) => selecionarCampanha(e.target.value)}>
                            <option value="">Selecione...</option>
                            {periodosLetivos.map((periodo) => (
                                <option key={periodo.id} value={periodo.id}>{periodo.descricao}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col xs={12} sm={6} md={2}>
                    <FormGroup>
                        <Label>Semestre*</Label>
                        <Input
                            type="select"
                            value={semestreId}
                            onChange={(e) => setSemestreId(e.target.value)}
                            disabled={!campanhaId}
                        >
                            <option value="">Selecione...</option>
                            {semestresDaCampanha.map((campanha) => (
                                <option key={campanha.semestre_id} value={campanha.semestre_id}>{campanha.semestre_descricao}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col xs={12} sm={6} md={2}>
                    <FormGroup>
                        <Label>Turma</Label>
                        <Input
                            type="select"
                            value={turmaId}
                            onChange={(e) => setTurmaId(e.target.value)}
                            disabled={!semestreId}
                        >
                            <option value="">Todas</option>
                            {turmas.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <FormGroup>
                        <Label>Situação</Label>
                        <Input type="select" value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                            <option value="">Todas</option>
                            {Object.entries(ROTULOS_SITUACAO).map(([valor, rotulo]) => (
                                <option key={valor} value={valor}>{rotulo}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col xs={12} sm={6} md={2}>
                    <FormGroup>
                        <Label>&nbsp;</Label>
                        <div className="d-flex align-items-center" style={{ height: 'calc(1.5em + .75rem)', gap: '.5rem' }}>
                            <Button className="flex-fill" color="primary" outline onClick={limpar}>Limpar</Button>
                            <Button className="flex-fill" color="primary" onClick={() => pesquisar()} disabled={!campanhaId || !semestreId || carregando}>Pesquisar</Button>
                        </div>
                    </FormGroup>
                </Col>
            </Row>
            {buscaRealizada && (
                <>
                    <div className="d-flex justify-content-around mb-3 mt-3">
                        <div className="text-center" style={estiloCard}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: CORES_SITUACAO.sem_envio }}>{contadores.sem_envio ?? 0}</div>
                            <div>Sem envio</div>
                        </div>
                        <div className="text-center" style={estiloCard}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: CORES_SITUACAO.pendente_revisao_manual }}>{contadores.pendente_revisao_manual ?? 0}</div>
                            <div>Pendente de revisão</div>
                        </div>
                        <div className="text-center" style={estiloCard}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: CORES_SITUACAO.conforme_automatico }}>{contadores.conforme_automatico ?? 0}</div>
                            <div>Conforme automático</div>
                        </div>
                        <div className="text-center" style={estiloCard}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: CORES_SITUACAO.conforme_revisado }}>{contadores.conforme_revisado ?? 0}</div>
                            <div>Conforme revisado</div>
                        </div>
                        <div className="text-center" style={estiloCard}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: CORES_SITUACAO.nao_conforme }}>{contadores.nao_conforme ?? 0}</div>
                            <div>Não conforme</div>
                        </div>
                    </div>

                    <Row form className="mb-3">
                        <Col xs={12} md={4}>
                            <FormGroup className="mb-0">
                                <Input
                                    type="text"
                                    placeholder="Buscar por nome ou matrícula..."
                                    value={buscaAluno}
                                    onChange={(e) => alterarBuscaAluno(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <table className="tabelaConsulta">
                        <thead>
                            <tr>
                                <th className="tabelaConsulta__celulaCabecalho">Matrícula</th>
                                <th className="tabelaConsulta__celulaCabecalho">Nome</th>
                                <th className="tabelaConsulta__celulaCabecalho">Semestre</th>
                                <th className="tabelaConsulta__celulaCabecalho">Grupo</th>
                                <th className="tabelaConsulta__celulaCabecalho">Aceitos</th>
                                <th className="tabelaConsulta__celulaCabecalho">Recusados</th>
                                <th className="tabelaConsulta__celulaCabecalho">Situação</th>
                                <th className="tabelaConsulta__celulaCabecalho">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentosExibidos.length === 0 ? (
                                <tr><td className="tabelaConsulta__celulaDados" colSpan={8}>Não há dados disponíveis</td></tr>
                            ) : documentosExibidos.map((linha) => (
                                <tr key={linha.aluno_id}>
                                    <td className="tabelaConsulta__celulaDados">{linha.matricula}</td>
                                    <td className="tabelaConsulta__celulaDados">{linha.nome}</td>
                                    <td className="tabelaConsulta__celulaDados">{linha.semestre}</td>
                                    <td className="tabelaConsulta__celulaDados">{linha.grupo}</td>
                                    <td className="tabelaConsulta__celulaDados">{linha.aceitos}</td>
                                    <td className="tabelaConsulta__celulaDados">{linha.recusados}</td>
                                    <td className="tabelaConsulta__celulaDados" style={{ color: CORES_SITUACAO[linha.situacao], fontWeight: 700 }}>
                                        {ROTULOS_SITUACAO[linha.situacao] || linha.situacao}
                                    </td>
                                    <td className="tabelaConsulta__celulaDados">
                                        <Button size="sm" color="link" className="font-weight-bold" onClick={() => abrirAnalise(linha)}>
                                            Analisar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <Button size="sm" disabled={paginaAtual <= 1} onClick={() => setPaginaExibida(paginaAtual - 1)}>«</Button>
                            {' '}Página {paginaAtual} de {totalPaginasExibidas}{' '}
                            <Button size="sm" disabled={paginaAtual >= totalPaginasExibidas} onClick={() => setPaginaExibida(paginaAtual + 1)}>»</Button>
                            {' '}— Registros: {documentosExibidos.length} de {documentosFiltrados.length}
                        </div>
                        <Button color="primary" size="sm" onClick={exportar}>Exportar XLSX</Button>
                    </div>
                </>
            )}
        </Col>
    );
}
